import { useEffect, useRef } from "react";
import { MaskedLines, Reveal, Overline, useTheme } from "./shared";

const FIELD_PALS = {
    night: { bg: [5, 5, 6], rod: [212, 235, 248], glow: [150, 205, 240] },
    day: { bg: [233, 229, 219], rod: [62, 92, 112], glow: [70, 110, 140] },
};

const STEPS = [
    "A person approaches.",
    "Lights respond. Mechanical elements move.",
    "A wall changes pattern. A sculpture opens.",
    "Light follows movement through the space.",
    "When they leave, it returns to rest.",
];

const ResponsiveField = () => {
    const ref = useRef(null);
    const wrapRef = useRef(null);
    const palRef = useRef(null);
    const { theme } = useTheme();

    useEffect(() => {
        if (!palRef.current) palRef.current = JSON.parse(JSON.stringify(FIELD_PALS[theme]));
        const cur = palRef.current;
        const tgt = FIELD_PALS[theme];
        const canvas = ref.current;
        const wrap = wrapRef.current;
        const ctx = canvas.getContext("2d");
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let w, h, dpr, raf, cells = [];
        const target = { x: 0.5, y: 0.5, active: 0, tActive: 0 };

        const lerp = () => {
            for (const k of ["bg", "rod", "glow"])
                for (let i = 0; i < 3; i++) cur[k][i] += (tgt[k][i] - cur[k][i]) * 0.06;
        };
        const rgb = (c, a) => `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`;

        const build = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = wrap.clientWidth;
            h = wrap.clientHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const cols = 16, rows = 8;
            cells = [];
            for (let r = 0; r < rows; r++)
                for (let c = 0; c < cols; c++)
                    cells.push({ x: ((c + 0.5) / cols) * w, y: ((r + 0.5) / rows) * h, ang: Math.random() * Math.PI, glow: 0 });
        };

        const draw = () => {
            lerp();
            target.active += (target.tActive - target.active) * 0.05;
            ctx.fillStyle = rgb(cur.bg, 0.5);
            ctx.fillRect(0, 0, w, h);
            const mx = target.x * w, my = target.y * h;
            for (const cell of cells) {
                const dx = mx - cell.x, dy = my - cell.y;
                const dist = Math.hypot(dx, dy);
                const desired = Math.atan2(dy, dx) + Math.PI / 2;
                const idle = Math.sin(Date.now() * 0.0004 + cell.x * 0.01 + cell.y * 0.01) * 0.4;
                const goal = desired * target.active + idle * (1 - target.active);
                let d = goal - cell.ang;
                while (d > Math.PI) d -= Math.PI * 2;
                while (d < -Math.PI) d += Math.PI * 2;
                cell.ang += d * 0.08;
                const reach = Math.max(0, 1 - dist / (w * 0.45)) * target.active;
                cell.glow += (reach - cell.glow) * 0.07;
                const len = 10 + cell.glow * 14;
                const a = 0.12 + cell.glow * 0.8;
                ctx.strokeStyle = rgb(cur.rod, a);
                ctx.lineWidth = 1;
                ctx.shadowBlur = cell.glow * 14;
                ctx.shadowColor = rgb(cur.glow, 0.6);
                ctx.beginPath();
                ctx.moveTo(cell.x - Math.sin(cell.ang) * len, cell.y - Math.cos(cell.ang) * len);
                ctx.lineTo(cell.x + Math.sin(cell.ang) * len, cell.y + Math.cos(cell.ang) * len);
                ctx.stroke();
            }
            ctx.shadowBlur = 0;
            raf = requestAnimationFrame(draw);
        };

        build();
        if (reduced) { cells.forEach((c) => (c.ang = 0.4)); }
        raf = requestAnimationFrame(draw);
        const onMove = (e) => {
            const r = wrap.getBoundingClientRect();
            target.x = (e.clientX - r.left) / r.width;
            target.y = (e.clientY - r.top) / r.height;
            target.tActive = 1;
        };
        const onLeave = () => { target.tActive = 0; };
        wrap.addEventListener("mousemove", onMove, { passive: true });
        wrap.addEventListener("mouseleave", onLeave);
        const onResize = () => build();
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(raf);
            wrap.removeEventListener("mousemove", onMove);
            wrap.removeEventListener("mouseleave", onLeave);
            window.removeEventListener("resize", onResize);
        };
    }, [theme]);

    return (
        <div ref={wrapRef} data-testid="interactive-field" className="relative h-[44vh] cursor-crosshair border border-white/10 md:h-[58vh]">
            <canvas ref={ref} className="absolute inset-0 h-full w-full" />
            <span className="pointer-events-none absolute bottom-5 left-6 font-mono-tech text-[10px] tracking-[0.35em] text-white/35">
                MOVE YOUR CURSOR — THE FIELD RESPONDS
            </span>
        </div>
    );
};

export const Interactive = () => (
    <section data-testid="interactive-section" className="relative border-t border-white/5 py-32 md:py-48">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <Overline>Interactive Installations</Overline>
            <h2 className="mt-10 max-w-5xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-7xl">
                <MaskedLines lines={["The installation knows", "you're there."]} />
            </h2>
            <div className="mt-20 grid gap-16 lg:grid-cols-[1fr_380px] lg:gap-24">
                <Reveal>
                    <ResponsiveField />
                </Reveal>
                <div className="flex flex-col justify-center space-y-8">
                    {STEPS.map((s, i) => (
                        <Reveal key={s} delay={i * 0.08} y={20}>
                            <div className="flex gap-6">
                                <span className="font-mono-tech text-[10px] tracking-[0.3em] text-white/30">{String(i + 1).padStart(2, "0")}</span>
                                <p className="text-base font-light leading-relaxed text-white/65">{s}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </section>
);
